/**
 * 収集カレンダー基本曜日設定サービス
 * モック/本番API切り替え対応
 * 足要書 8.7 収集カレンダー基本曜日設定 に準拠
 */

import { USE_MOCK_API } from '../config.js'

// モックデータ
let mockPatterns = [
    {
        id: 1,
        districtId: 1,
        districtName: '第1収集地区',
        year: 2024,
        wasteTypeId: 2,
        wasteTypeName: '可燃ごみ',
        patternType: 'weekly',
        settings: {
            daysOfWeek: [1, 3, 5] // 月, 水, 金
        },
        createdAt: '2024-03-01T10:00:00.000Z',
        updatedAt: '2024-03-01T10:00:00.000Z'
    },
    {
        id: 2,
        districtId: 1,
        districtName: '第1収集地区',
        year: 2024,
        wasteTypeId: 1,
        wasteTypeName: 'プラスチック',
        patternType: 'specific-week',
        settings: {
            patterns: [
                { dayOfWeek: 4, weeks: [1, 3] } // 第1・3木曜日
            ]
        },
        createdAt: '2024-03-01T10:00:00.000Z',
        updatedAt: '2024-03-01T10:00:00.000Z'
    }
]

let nextId = mockPatterns.length + 1

// モック用ユーティリティ
function simulateDelay(ms = 500) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

// =============================================================================
// モックAPI実装
// =============================================================================

const mockApi = {
    /**
     * 基本パターン取得
     */
    async getCollectionPatterns(districtId, year) {
        await simulateDelay(500)
        console.log(`[Mock API] パターン取得: 地区${districtId} ${year}年度`)

        const patterns = mockPatterns.filter(p =>
            p.districtId === Number(districtId) && p.year === Number(year)
        )

        return {
            success: true,
            data: JSON.parse(JSON.stringify(patterns))
        }
    },

    /**
     * 基本パターン作成
     */
    async createCollectionPattern(data) {
        await simulateDelay(500)
        console.log('[Mock API] パターン作成:', data)

        const newPattern = {
            id: nextId++,
            ...data,
            districtName: `地区${data.districtId}`, // 簡易的
            wasteTypeName: `種別${data.wasteTypeId}`, // 簡易的
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }

        mockPatterns.push(newPattern)

        return {
            success: true,
            data: newPattern,
            message: 'パターンを作成しました'
        }
    },

    /**
     * 基本パターン更新
     */
    async updateCollectionPattern(id, data) {
        await simulateDelay(500)
        console.log(`[Mock API] パターン更新 ID:${id}`, data)

        const index = mockPatterns.findIndex(p => p.id === Number(id))
        if (index === -1) {
            throw new Error('Pattern not found')
        }

        mockPatterns[index] = {
            ...mockPatterns[index],
            ...data,
            updatedAt: new Date().toISOString()
        }

        return {
            success: true,
            data: mockPatterns[index],
            message: 'パターンを更新しました'
        }
    },

    /**
     * 基本パターン削除
     */
    async deleteCollectionPattern(id) {
        await simulateDelay(400)
        console.log(`[Mock API] パターン削除 ID:${id}`)

        const index = mockPatterns.findIndex(p => p.id === Number(id))
        if (index !== -1) {
            mockPatterns.splice(index, 1)
        }

        return {
            success: true,
            message: 'パターンを削除しました'
        }
    },

    /**
     * カレンダー一括適用
     */
    async applyPatternsToCalendar(data) {
        await simulateDelay(1000)
        console.log('[Mock API] カレンダー一括適用:', data)

        return {
            success: true,
            appliedDates: 365,
            overwrittenDates: 50,
            message: 'カレンダーにパターンを適用しました'
        }
    }
}

// =============================================================================
// 本番API実装
// =============================================================================

const productionApi = {
    async getCollectionPatterns(districtId, year) {
        const response = await fetch(`/api/system-settings/collection-patterns?district=${districtId}&year=${year}`)
        if (!response.ok) throw new Error('API Error')
        return await response.json()
    },

    async createCollectionPattern(data) {
        const response = await fetch('/api/system-settings/collection-patterns', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
        if (!response.ok) throw new Error('API Error')
        return await response.json()
    },

    async updateCollectionPattern(id, data) {
        const response = await fetch(`/api/system-settings/collection-patterns/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
        if (!response.ok) throw new Error('API Error')
        return await response.json()
    },

    async deleteCollectionPattern(id) {
        const response = await fetch(`/api/system-settings/collection-patterns/${id}`, {
            method: 'DELETE'
        })
        if (!response.ok) throw new Error('API Error')
        return await response.json()
    },

    async applyPatternsToCalendar(data) {
        const response = await fetch('/api/system-settings/collection-patterns/apply-to-calendar', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
        if (!response.ok) throw new Error('API Error')
        return await response.json()
    }
}

// =============================================================================
// 統合サービスインターフェース
// =============================================================================

const apiService = USE_MOCK_API ? mockApi : productionApi

export default {
    getCollectionPatterns: async (districtId, year) => {
        return await apiService.getCollectionPatterns(districtId, year)
    },
    createCollectionPattern: async (data) => {
        return await apiService.createCollectionPattern(data)
    },
    updateCollectionPattern: async (id, data) => {
        return await apiService.updateCollectionPattern(id, data)
    },
    deleteCollectionPattern: async (id) => {
        return await apiService.deleteCollectionPattern(id)
    },
    applyPatternsToCalendar: async (request) => {
        return await apiService.applyPatternsToCalendar(request)
    }
}
